import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isTokenExpired } from '../utils/jwt';
import { setCookie, deleteCookie } from '../utils/cookies';

interface SignUpResponse {
  requiresVerification?: boolean;
  message?: string;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  idToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isFamilyMode: boolean;
  familyModeEnabledAt: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<SignUpResponse | void>;
  signOut: () => void;
  signOutFromAllDevices: () => Promise<void>;
  enterFamilyMode: () => void;
  exitFamilyMode: () => void;
  verifyEmail: (email: string, code: string) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<any>;
  refreshAccessToken: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (previousPassword: string, newPassword: string) => Promise<void>;
  clearError: () => void;
  initializeTokenRefresh: () => void;
}

import { getApiUrl, getApiKey } from '../utils/apiConfig';

// Get API configuration (throws if not configured)
const API_URL = getApiUrl();
const API_KEY = getApiKey();

// Helper to get headers with optional API key
const getHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (API_KEY) {
    headers['X-Api-Key'] = API_KEY; // API Gateway expects X-Api-Key (capitalized)
  }
  return headers;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      // Initialize: validate that we have tokens, otherwise set isAuthenticated to false
      const initialState = {
        accessToken: null,
        refreshToken: null,
        idToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        isFamilyMode: false,
        familyModeEnabledAt: null,
      };

      return {
        ...initialState,

        signIn: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/auth/signin`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ email, password }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Sign in failed');
          }

          const data = await response.json();
          // Only set authenticated if we have all tokens
          const hasAllTokens = !!(data.accessToken && data.refreshToken && data.idToken);
          
          // Set tokens in cookies for cross-domain access (runow.ai landing page)
          if (hasAllTokens) {
            setCookie('runow-id-token', data.idToken);
            setCookie('runow-access-token', data.accessToken);
            // Don't store refresh token in cookie for security
          }
          
          set({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            idToken: data.idToken,
            isAuthenticated: hasAllTokens,
            isLoading: false,
            error: null,
          });
        } catch (err: any) {
          set({
            isLoading: false,
            error: err.message || 'Failed to sign in',
            isAuthenticated: false,
            accessToken: null,
            refreshToken: null,
            idToken: null,
          });
          throw err;
        }
      },

      signUp: async (email: string, password: string, name?: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/auth/signup`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ email, password, name }),
          });

          const data = await response.json();
          
          // Handle 200 response (user exists but unverified - verification code resent)
          if (response.ok) {
            set({ isLoading: false, error: null });
            return data;
          }

          // Handle error responses
          throw new Error(data.message || 'Sign up failed');
        } catch (err: any) {
          set({
            isLoading: false,
            error: err.message || 'Failed to sign up',
          });
          throw err;
        }
      },

      verifyEmail: async (email: string, code: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/auth/verify-email`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ email, confirmationCode: code }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Verification failed');
          }

          set({ isLoading: false, error: null });
        } catch (err: any) {
          set({
            isLoading: false,
            error: err.message || 'Failed to verify email',
          });
          throw err;
        }
      },

      resendVerificationEmail: async (email: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/auth/resend-verification-email`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ email }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to resend verification email');
          }

          const data = await response.json();
          set({ isLoading: false, error: null });
          return data;
        } catch (err: any) {
          set({
            isLoading: false,
            error: err.message || 'Failed to resend verification email',
          });
          throw err;
        }
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          set({ isAuthenticated: false, accessToken: null, idToken: null });
          return;
        }

        try {
          const response = await fetch(`${API_URL}/auth/refresh`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ refreshToken }),
          });

          if (!response.ok) {
            throw new Error('Token refresh failed');
          }

          const data = await response.json();
          // Only set authenticated if we have all tokens
          const hasAllTokens = !!(data.accessToken && data.idToken && refreshToken);
          
          // Update cookies with new tokens
          if (hasAllTokens) {
            setCookie('runow-id-token', data.idToken);
            setCookie('runow-access-token', data.accessToken);
          }
          
          set({
            accessToken: data.accessToken,
            idToken: data.idToken,
            isAuthenticated: hasAllTokens,
          });
        } catch (err) {
          set({ isAuthenticated: false, accessToken: null, refreshToken: null, idToken: null });
        }
      },

      signOut: () => {
        // Clear cookies
        deleteCookie('runow-id-token');
        deleteCookie('runow-access-token');
        
        set({
          accessToken: null,
          refreshToken: null,
          idToken: null,
          isAuthenticated: false,
          error: null,
          isFamilyMode: false,
          familyModeEnabledAt: null,
        });
        // Clear events when signing out
        const { useEventStore } = require('./eventStore');
        useEventStore.getState().clearEvents();
      },

      enterFamilyMode: () => {
        set({
          isFamilyMode: true,
          familyModeEnabledAt: new Date().toISOString(),
        });
      },

      exitFamilyMode: () => {
        set({
          isFamilyMode: false,
          familyModeEnabledAt: null,
        });
      },

      signOutFromAllDevices: async () => {
        const { accessToken } = get();
        
        // Try to call backend to invalidate all sessions
        // Even if this fails, we'll still clear local state
        if (accessToken) {
          try {
            const headers: HeadersInit = {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            };
            if (API_KEY) {
              headers['X-Api-Key'] = API_KEY;
            }

            await fetch(`${API_URL}/auth/signout`, {
              method: 'POST',
              headers,
            });
            // Don't check response.ok - we'll clear local state regardless
          } catch (err) {
            // Ignore errors - network issues, expired token, etc.
            // We'll still clear local state
            console.error('Sign out from all devices API call failed:', err);
          }
        }
        
        // Always clear local state, regardless of API call success/failure
        deleteCookie('runow-id-token');
        deleteCookie('runow-access-token');
        
        set({
          accessToken: null,
          refreshToken: null,
          idToken: null,
          isAuthenticated: false,
          error: null,
          isFamilyMode: false,
          familyModeEnabledAt: null,
        });
        // Clear events when signing out
        const { useEventStore } = require('./eventStore');
        useEventStore.getState().clearEvents();
      },

      forgotPassword: async (email: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/auth/forgot-password`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ email }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to send password reset code');
          }

          const data = await response.json();
          set({ isLoading: false, error: null });
          return data;
        } catch (err: any) {
          set({
            isLoading: false,
            error: err.message || 'Failed to send password reset code',
          });
          throw err;
        }
      },

      confirmForgotPassword: async (email: string, code: string, newPassword: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/auth/confirm-forgot-password`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ email, confirmationCode: code, newPassword }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to reset password');
          }

          const data = await response.json();
          set({ isLoading: false, error: null });
          return data;
        } catch (err: any) {
          set({
            isLoading: false,
            error: err.message || 'Failed to reset password',
          });
          throw err;
        }
      },

      changePassword: async (previousPassword: string, newPassword: string) => {
        set({ isLoading: true, error: null });
        try {
          const { accessToken } = get();
          if (!accessToken) {
            throw new Error('Not authenticated');
          }

          const headers: HeadersInit = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          };
          if (API_KEY) {
            headers['X-Api-Key'] = API_KEY;
          }

          const response = await fetch(`${API_URL}/auth/change-password`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ previousPassword, proposedPassword: newPassword }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to change password');
          }

          const data = await response.json();
          set({ isLoading: false, error: null });
          return data;
        } catch (err: any) {
          set({
            isLoading: false,
            error: err.message || 'Failed to change password',
          });
          throw err;
        }
      },

      clearError: () => {
        set({ error: null });
      },

      initializeTokenRefresh: () => {
        // Set up periodic token refresh check (every 10 minutes)
        // This ensures tokens stay fresh while the app is running
        const checkAndRefreshTokens = async () => {
          const state = get();
          if (!state.isAuthenticated || !state.refreshToken) {
            return; // Not authenticated, no need to refresh
          }

          // Check if tokens are expired or will expire within 10 minutes
          const idTokenExpired = isTokenExpired(state.idToken, 600); // 10 minute buffer
          const accessTokenExpired = isTokenExpired(state.accessToken, 600);

          if (idTokenExpired || accessTokenExpired) {
            try {
              await state.refreshAccessToken();
            } catch (error) {
              console.error('Periodic token refresh failed:', error);
              // Don't sign out on periodic refresh failure - let the next API call handle it
            }
          }
        };

        // Check immediately, then every 10 minutes
        checkAndRefreshTokens();
        const intervalId = setInterval(checkAndRefreshTokens, 10 * 60 * 1000); // 10 minutes

        // Store interval ID for cleanup (though in practice, the app runs continuously)
        // If needed, we could add a cleanup method, but for a mobile app this is fine
        return () => clearInterval(intervalId);
      },
      };
    },
    {
      name: 'auth-storage',
      partialize: (state: AuthState) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        idToken: state.idToken,
        isFamilyMode: state.isFamilyMode,
        familyModeEnabledAt: state.familyModeEnabledAt,
        // Don't persist isAuthenticated - compute it from tokens on load
        // isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => async (state) => {
        // On rehydration, validate that we have tokens before setting isAuthenticated
        if (state) {
          const hasValidTokens = !!(state.accessToken && state.refreshToken && state.idToken);
          if (!hasValidTokens) {
            state.isAuthenticated = false;
            // Clear tokens if they're invalid
            state.accessToken = null;
            state.refreshToken = null;
            state.idToken = null;
            // Clear cookies too
            deleteCookie('runow-id-token');
            deleteCookie('runow-access-token');
          } else {
            // Set cookies when restoring from localStorage (for cross-domain access)
            if (state.idToken) setCookie('runow-id-token', state.idToken);
            if (state.accessToken) setCookie('runow-access-token', state.accessToken);
            
            // Check if tokens are expired or about to expire (within 5 minutes)
            const idTokenExpired = isTokenExpired(state.idToken, 300); // 5 minute buffer
            const accessTokenExpired = isTokenExpired(state.accessToken, 300);
            
            // If either token is expired, try to refresh
            if (idTokenExpired || accessTokenExpired) {
              // Set authenticated to true initially (optimistic) - refresh will update if it fails
              state.isAuthenticated = true;
              
              // Refresh token asynchronously - don't block app startup
              // Use setTimeout to ensure this happens after rehydration completes
              setTimeout(async () => {
                try {
                  // Get the store instance to call refreshAccessToken
                  const store = useAuthStore.getState();
                  await store.refreshAccessToken();
                } catch (error) {
                  console.error('Failed to refresh token on app startup:', error);
                  // If refresh fails, clear tokens and sign out
                  const store = useAuthStore.getState();
                  store.signOut();
                }
              }, 0);
            } else {
              // Tokens are still valid
              state.isAuthenticated = true;
            }
          }
        }
      },
    }
  )
);
